import { Box } from "@mui/material";
import { useMemo } from "react";
import type { Props } from 'recharts/types/component/DefaultLegendContent'
import { CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { monthsAbbreviated } from "src/constants";
import { authUser$ } from "src/framework/components/navigations/MenuUser";
import { useObservable } from "src/framework/store/storeUtil";
import { ExtraInfoUsuario } from "src/providers/cadastros/ContaProvider/types";
import { CacheType, contaUsuario$ } from "src/store/global.store";
import { ContaUsuario } from "src/store/global.store.types";
import { TIPO_CONTA_INTEGRADOR } from "src/types";

interface PropostasTempoGraficoItemProps {
  "Data": string;
  "Valor dos Projetos": number;
  "Valor do Distribuidor": number;
}

interface PropostasTempoGraficoProps {
  data: PropostasTempoGraficoItemProps[]
  isLoading: boolean
}

function formatChartLabelAxisValue(value: number) {
  const number = Number(value) || 0;
  if (number < 1e3) return Math.round(number);
  if (number >= 1e3 && number < 1e6) return `${Math.round(number / 1000)}k`;
  if (number >= 1e6 && number < 2e6) return `${(Math.round(number) / 1000000).toFixed(1)}mi`;
  if (number >= 2e6 && number < 1e9) return `${(Math.round(number) / 1000000)}mi`;
  if (number >= 1e9 && number < 2e9) return `${(Math.round(number) / 1000000000).toFixed(1)}bi`;
  if (number >= 2e9 && number < 1e12) return `${Math.round(number) / 1000000000}bi`;
  if (number >= 1e12 && number < 2e12) return `${(Math.round(number) / 1000000000000).toFixed(1)}tri`;
  if (number >= 2e12 && number < 1e15) return `${Math.round(number) / 1000000000000}tri`;
  return number
}

function CustomXTick({ x, y, payload }: any){
  const tick = useMemo(()=>{
    const splitted = payload.value.split('/')
    if (splitted.length === 3) return {
      dia: splitted[0],
      mes: splitted[1],
      ano: splitted[2]
    }


    if (splitted.length === 2) return {
      mes: splitted[0],
      ano: splitted[1]
    }

    return { dia: '', mes: '', ano: '' }
  },[payload])

  if (tick.dia) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={-30}
          y={12}
          dy={0}
          dx={0}
          textAnchor="middle"
          fill="#000"
          style={{ fontSize: 12 }}
        >
          {`${tick.dia}/${tick.mes}/${tick.ano}`}
        </text>
      </g>
    )
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-10}
        y={12}
        dy={0}
        dx={0}
        textAnchor="middle"
        fill="#000"
        style={{ fontSize: 12 }}
      >
        {monthsAbbreviated[Number(tick.mes) - 1]}
      </text>
      <text
        x={0}
        y={10}
        dy={14}
        dx={-7}
        textAnchor="middle"
        fill="#000"
        style={{ fontSize: 12 }}
      >
        {tick.ano.substring(2,4)}
      </text>
    </g>
  )
}

function CustomYTick({ x, y, payload }: any){
  const formatted = useMemo(() => {
    const formattedText = formatChartLabelAxisValue(payload.value)
    const isNumber = !Number.isNaN(Number(formattedText)) 
    if (isNumber) return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL'}).format(payload.value)
    return `R$ ${formattedText}`
  },[payload.value])

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={0}
        textAnchor="end"
        fill="#666"
        style={{ fontSize: 12 }}
      >
        {formatted}
      </text>
    </g>
  )
}

function CustomLegend(props: Props){
  const payloads = props.payload || []
  return (
      <Box display="flex" alignItems={"center"} justifyContent={"center"} marginTop={"15px"}>
        {payloads.map(payload => (
            <Box marginRight={3} display={"flex"} alignItems={"center"} justifyContent={"center"}>
              <Box width={"20px"} height={"10px"} marginRight={"5px"} bgcolor={payload.color} />
              <span style={{ color: "#000" }}>{payload.value}</span>
            </Box>
          )
        )}
      </Box>
  )
}

export function ValorTempoGrafico({ data, isLoading }: PropostasTempoGraficoProps){
  const authUser = useObservable<ExtraInfoUsuario | undefined>(authUser$)
  const contaUsuario = useObservable<CacheType<ContaUsuario | null>>(contaUsuario$)
  
  const isIntegradorSolar = useMemo(()=>{
    return contaUsuario?.data?.conta?.tipo_conta_id === TIPO_CONTA_INTEGRADOR
  },[contaUsuario])

  if (isLoading) {
    return (
      <Box display="flex" alignItems={"center"} justifyContent={"center"} height={"150px"}>
        <span>Buscando...</span>
      </Box>
    )
  }

  if (!data.length) {
    return <h2>Não há dados</h2>
  }

  return (
    <ResponsiveContainer>
      <LineChart data={data}>
        <XAxis dataKey={"Data"} tick={<CustomXTick />} />
        <YAxis width={80} tick={<CustomYTick />} />
        <Tooltip formatter={v => Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0))} />
        <Legend content={<CustomLegend />} />
        <CartesianGrid opacity={"0.5"} />
        <Line
          isAnimationActive={false}
          dataKey={'Valor dos Projetos'}
          fill="rgba(139, 230, 138, 0.50)"
          stroke="rgba(139, 230, 138, 1)"
          strokeWidth={"2"}
          >
          {data &&
            data.map((entry) => {
              return (
                <Cell key={`cell-${entry.Data}`} />
                );
              })}
        </Line>
        {authUser?.isDistribuidor && (
          <>
            <Line
              isAnimationActive={false}
              dataKey={'Valor do Distribuidor'}
              fill="rgba(98, 127, 135, 0.50)"
              stroke="rgba(98, 127, 135, 1)"
              strokeDasharray="10 3"
              strokeWidth={"2"}
              >
              {data &&
                data.map((entry) => {
                  return (
                    <Cell key={`cell-${entry.Data}`} />
                    );
                  })}
            </Line>            
          </>
        )}
        {(authUser?.isRevenda || isIntegradorSolar) && (
          <>            
            <Line
              isAnimationActive={false}
              dataKey={'Valor dos Serviços'}
              fill="rgba(98, 127, 135, 0.50)"
              stroke="rgba(98, 127, 135, 1)"
              strokeDasharray="10 3"
              strokeWidth={"2"}
              >
              {data &&
                data.map((entry) => {
                  return (
                    <Cell key={`cell-${entry.Data}`} />
                    );
                  })}
            </Line>
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  )
}